import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ShieldCheck, CheckCircle2, Lock, Loader2 } from "lucide-react";
import { useState, useMemo } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useCart, type CartLine } from "@/lib/cart/cart-store";
import { createPayfastCartOrder } from "@/lib/api/cart-payfast.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/checkout")({
  head: () => ({
    meta: [
      { title: "Secure checkout | Nkadimeng Danny Legal" },
      {
        name: "description",
        content:
          "Complete your order securely with PayFast — pay by card, Instant EFT, or wallet. All payments are processed by South Africa's leading payment gateway.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CheckoutPage,
});

function CheckoutPage() {
  const { rows, totalZar, lines, hydrated } = useCart();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const customerValid = useMemo(
    () => fullName.trim().length >= 2 && /.+@.+\..+/.test(email),
    [fullName, email],
  );

  if (hydrated && rows.length === 0) {
    return (
      <section className="container-narrow py-16 text-center">
        <h1 className="font-display text-3xl text-navy">Your cart is empty</h1>
        <p className="mt-3 text-muted-foreground">
          Add a study pack first, then come back here to pay.
        </p>
        <Link
          to="/contracts"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-navy-foreground"
        >
          Browse contracts
        </Link>
      </section>
    );
  }

  return (
    <section className="container-narrow py-12">
      <Link to="/cart" className="inline-flex items-center gap-1 text-sm text-navy hover:text-gold">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to cart
      </Link>
      <h1 className="mt-3 font-display text-3xl text-navy md:text-4xl">Secure checkout</h1>
      <p className="mt-2 max-w-xl text-sm text-muted-foreground">
        Enter your details and pay securely via PayFast. We start preparing your
        notes the moment payment is confirmed and email them to you within the
        stated turnaround.
      </p>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_380px]">
        <div className="space-y-6">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <h2 className="font-display text-lg text-navy">Your details</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              We'll email your notes to this address. Keep it accurate.
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <label className="grid gap-1.5 text-sm sm:col-span-2">
                <span className="font-medium text-navy">
                  Full name <span className="text-gold">*</span>
                </span>
                <input
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="grid gap-1.5 text-sm">
                <span className="font-medium text-navy">
                  Email <span className="text-gold">*</span>
                </span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="grid gap-1.5 text-sm">
                <span className="font-medium text-navy">Phone (optional)</span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </label>
            </div>
          </div>

          <PayfastCheckoutPanel
            customerValid={customerValid}
            lines={lines}
            customer={{
              full_name: fullName.trim(),
              email: email.trim(),
              phone: phone.trim() || undefined,
            }}
            totalZar={totalZar}
          />
        </div>

        <aside className="h-fit rounded-2xl border border-border bg-card p-6 shadow-soft">
          <h2 className="font-display text-lg text-navy">Order summary</h2>
          <ul className="mt-4 space-y-3 text-sm">
            {rows.map((r) => (
              <li
                key={`${r.itemId}-${r.topic ?? ""}`}
                className="flex justify-between gap-3 border-b border-border pb-3 last:border-0"
              >
                <div className="min-w-0">
                  <p className="font-medium text-navy">
                    {r.item.name}{" "}
                    {r.qty > 1 && (
                      <span className="text-muted-foreground">× {r.qty}</span>
                    )}
                  </p>
                  {r.topic && (
                    <p className="text-xs text-muted-foreground truncate">{r.topic}</p>
                  )}
                </div>
                <p className="shrink-0 font-medium">R{r.lineTotalZar}</p>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex justify-between border-t border-border pt-3">
            <span className="font-semibold text-navy">Total</span>
            <span className="font-display text-xl text-navy">R{totalZar}</span>
          </div>
          <div className="mt-5 flex items-center gap-2 rounded-md bg-secondary/60 px-3 py-2 text-xs text-navy">
            <CheckCircle2 className="h-3.5 w-3.5 text-gold" />
            Delivery starts the moment PayFast confirms payment.
          </div>
        </aside>
      </div>
    </section>
  );
}

function PayfastCheckoutPanel({
  customerValid,
  lines,
  customer,
  totalZar,
}: {
  customerValid: boolean;
  lines: CartLine[];
  customer: { full_name: string; email: string; phone?: string };
  totalZar: number;
}) {
  const navigate = useNavigate();
  const createOrder = useServerFn(createPayfastCartOrder);
  const [loading, setLoading] = useState(false);

  async function payNow() {
    if (!customerValid || lines.length === 0) return;
    setLoading(true);
    try {
      const { reference } = await createOrder({
        data: {
          lines: lines.map((l) => ({
            itemId: l.itemId,
            qty: l.qty,
            topic: l.topic ?? null,
            note: null,
          })),
          customer: {
            full_name: customer.full_name,
            email: customer.email,
            phone: customer.phone ?? null,
          },
        },
      });
      const { launchPayfastCheckout } = await import("@/lib/payfast-launch");
      await launchPayfastCheckout(reference);
    } catch (e: any) {
      toast.error("Could not start PayFast checkout", {
        description: e?.message ?? String(e),
      });
      setLoading(false);
    }
  }

  return (
    <div className="rounded-2xl border border-gold/40 bg-card p-6 shadow-soft">
      <h2 className="font-display text-lg text-navy">Pay securely with PayFast</h2>
      <p className="mt-2 text-xs text-muted-foreground">
        You'll be redirected to PayFast to pay in ZAR by card, Instant EFT,
        SnapScan, Zapper, Masterpass, or PayFast wallet. We never see your card
        details.
      </p>

      {!customerValid && (
        <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Enter your full name and email above to enable the payment button.
        </p>
      )}

      <button
        type="button"
        disabled={!customerValid || loading}
        onClick={payNow}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#13B5EA] px-4 py-3 text-sm font-semibold text-white shadow-sm hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Lock className="h-4 w-4" />
        )}
        {loading ? "Redirecting to PayFast…" : `Pay R${totalZar} with PayFast`}
      </button>

      <ul className="mt-4 space-y-1.5 text-[11px] text-muted-foreground">
        <li className="flex items-start gap-1.5">
          <ShieldCheck className="mt-0.5 h-3 w-3 text-gold" /> Verified PayFast
          merchant — PCI-DSS Level 1 secure.
        </li>
        <li className="flex items-start gap-1.5">
          <ShieldCheck className="mt-0.5 h-3 w-3 text-gold" /> Charged in ZAR.
          No currency conversion fees.
        </li>
        <li className="flex items-start gap-1.5">
          <ShieldCheck className="mt-0.5 h-3 w-3 text-gold" /> Order released
          automatically the moment PayFast confirms payment.
        </li>
      </ul>

      <button
        type="button"
        onClick={() => navigate({ to: "/cart" })}
        className="mt-4 text-xs text-muted-foreground underline hover:text-navy"
      >
        ← Edit cart
      </button>
    </div>
  );
}
